// Keep preview section responses in the same theme as the visible page.
function ldThemeRequestURL(value) {
  const url = new URL(value, location.origin);
  const theme = document.documentElement.dataset;
  if (url.origin === location.origin && theme.ldThemeId && theme.ldThemeRole !== 'main') url.searchParams.set('preview_theme_id', theme.ldThemeId);
  return url;
}
function ldCartSectionURL() {
  const url = ldThemeRequestURL(location.pathname);
  return url.pathname + url.search;
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  static backgroundReady() {
    if (!this.readyPromise) {
      this.readyPromise = new Promise(resolve => {
        if (document.readyState !== 'loading') resolve();
        else document.addEventListener('DOMContentLoaded', resolve, { once: true });
      }).then(() => Promise.all(Array.from(document.querySelectorAll('.product-card-wrapper .card__media img:first-child')).filter(image => {
        const rect = image.getBoundingClientRect();
        return rect.width > 0 && rect.bottom > 0 && rect.top < innerHeight;
      }).map(image => image.complete ? Promise.resolve() : new Promise(resolve => {
        const done = () => { clearTimeout(timer); image.removeEventListener('load', done); image.removeEventListener('error', done); resolve(); };
        const timer = setTimeout(done, 3000);
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      })))).then(() => new Promise(resolve => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 1500 });
        else setTimeout(resolve, 200);
      }));
    }
    return this.readyPromise;
  }

  isVisibleWholesale() {
    if (!this.isConnected || !this.wholesaleOpener?.isConnected) return false;
    const card = this.wholesaleOpener.closest('.product-card-wrapper');
    if (!card) return false;
    const rect = card.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  }

  static drainWholesale() {
    this.pendingWholesale ||= [];
    this.activeWholesale ||= 0;
    while (this.activeWholesale < 2 && this.pendingWholesale.length) {
      const job = this.pendingWholesale.shift();
      const modal = job.modal;
      if (!modal.isConnected || !modal.wholesaleOpener?.isConnected || (!job.priority && !modal.isVisibleWholesale())) {
        modal.queuedWholesale = null;
        job.resolve();
        continue;
      }
      job.started = true;
      this.activeWholesale++;
      Promise.resolve().then(() => modal.fetchWholesale()).catch(() => {}).finally(() => {
        this.activeWholesale--;
        modal.queuedWholesale = null;
        job.resolve();
        this.drainWholesale();
      });
    }
  }

  loadWholesale(priority = true) {
    if (this.querySelector('ld-bulk-order')) return Promise.resolve();
    if (this.queuedWholesale) {
      const job = this.queuedWholesale;
      if (priority && !job.started) {
        job.priority = true;
        const index = BulkModal.pendingWholesale.indexOf(job);
        if (index >= 0) BulkModal.pendingWholesale.splice(index, 1);
        BulkModal.pendingWholesale.unshift(job);
      }
      return job.promise;
    }
    const job = { modal: this, priority, started: false };
    job.promise = new Promise(resolve => { job.resolve = resolve; });
    this.queuedWholesale = job;
    BulkModal.pendingWholesale ||= [];
    if (priority) BulkModal.pendingWholesale.unshift(job);
    else BulkModal.pendingWholesale.push(job);
    BulkModal.drainWholesale();
    return job.promise;
  }


  connectedCallback() {
    if (this.dataset.wholesale === 'true') {
      this.wholesaleRetryHandler ||= event => {
        if (event.target.closest('[data-ld-bulk-retry]')) this.loadWholesale();
      };
      this.removeEventListener('click', this.wholesaleRetryHandler);
      this.addEventListener('click', this.wholesaleRetryHandler);
      this.wholesaleOpener = document.getElementById(`QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`);
      this.wholesaleIntentHandler ||= () => this.loadWholesale(true);
      this.wholesaleOpener?.addEventListener('pointerenter', this.wholesaleIntentHandler);
      this.wholesaleOpener?.addEventListener('focusin', this.wholesaleIntentHandler);
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        BulkModal.backgroundReady().then(() => {
          if (this.isVisibleWholesale()) this.loadWholesale(false);
        });
      });
      const card = this.wholesaleOpener?.closest('.product-card-wrapper');
      if (card) this.observer.observe(card);
      return;
    }
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() !== '') return;
      const productUrl = this.dataset.url.split('?')[0];
      fetch(`${productUrl}?section_id=bulk-quick-order-list`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.querySelector('.quick-order-list-container');
          if (source) this.innerHTML = source.parentNode.innerHTML;
        })
        .catch(console.error);
    };
    this.observer = new IntersectionObserver(handleIntersection);
    const opener = document.getElementById(`QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`);
    if (opener) this.observer.observe(opener);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.wholesaleOpener?.removeEventListener('pointerenter', this.wholesaleIntentHandler);
    this.wholesaleOpener?.removeEventListener('focusin', this.wholesaleIntentHandler);
  }

  fetchWholesale() {
    if (this.querySelector('ld-bulk-order')) return Promise.resolve();
    if (this.loadingPromise) return this.loadingPromise;
    const preview = this.querySelector('template[data-ld-bulk-preview]');
    if (preview) {
      this.innerHTML = preview.innerHTML;
      return Promise.resolve();
    }
    this.replaceChildren();
    const status = document.createElement('p');
    status.className = 'ld-bulk-modal__status';
    status.setAttribute('role', 'status');
    status.textContent = 'Loading product options…';
    this.append(status);
    this.setAttribute('aria-busy', 'true');
    const url = new URL(this.dataset.url, window.location.origin);
    url.searchParams.set('section_id', 'bulk-quick-order-list');
    this.loadingPromise = fetch(ldThemeRequestURL(url))
      .then((response) => {
        if (!response.ok) throw new Error('Bulk options request failed');
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const form = html.querySelector('ld-bulk-order');
        const source = form?.closest('.quick-order-list-container');
        if (!source) throw new Error('Bulk options unavailable for this session');
        this.innerHTML = source.parentNode.innerHTML;
      })
      .catch(() => {
        status.textContent = 'Product options could not load. Please try again or view the product.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button--secondary';
        retry.dataset.ldBulkRetry = '';
        retry.textContent = 'Try Again';
        const details = document.createElement('a');
        details.href = this.dataset.url;
        details.className = 'ld-bulk-modal__details link';
        details.textContent = 'View Product Details';
        this.append(retry, details);
      })
      .finally(() => {
        this.removeAttribute('aria-busy');
        this.loadingPromise = null;
      });
    return this.loadingPromise;
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) {
        modal.show(button);
        if (modal.classList.contains('ld-bulk-modal')) modal.querySelector('bulk-modal')?.loadWholesale();
      }
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// Lavish Daily: color-by-color bulk ordering in collection quick-add modals.
if (!customElements.get('ld-bulk-order')) {
  customElements.define('ld-bulk-order', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rows = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.groups = Array.from(this.querySelectorAll('[data-option-index]'));
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.status = this.querySelector('.ld-bulk__status');
      this.selection = {};
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:this.dataset.currency || 'USD'});
      this.rows.forEach(row => { row.variantOptions = JSON.parse(row.dataset.options); });
      const initial = this.rows.find(row => row.dataset.variantId === this.dataset.initialVariant && row.dataset.available === 'true') || this.rows.find(row => row.dataset.available === 'true') || this.rows[0];
      if (!initial || !this.form || !this.submitButton) return;
      this.groups.forEach(group => { this.selection[group.dataset.optionIndex] = initial.variantOptions[Number(group.dataset.optionIndex)]; });
      this.addEventListener('focusin', event => {
        if (event.target.matches('.quantity__input')) event.target.select();
      });
      this.addEventListener('focusout', event => {
        const input = event.target;
        if (!input.matches('.quantity__input')) return;
        const value = input.value.trim();
        if (value === '') input.value = '0';
        else if (/^\d+$/.test(value) && Number.isSafeInteger(Number(value))) input.value = String(Number(value));
        this.updateTotals();
      });
      this.addEventListener('click', event => {
        if (event.target.matches('.quantity__input')) event.target.select();
        const option = event.target.closest('[data-option-value]');
        if (option) {
          const group = option.closest('[data-option-index]');
          this.selection[group.dataset.optionIndex] = option.dataset.optionValue;
          this.renderSelection();
        }
        const step = event.target.closest('[data-step]');
        if (step && !this.busy) {
          this.clearToolUndo();
          const input = step.parentElement.querySelector('input');
          const current = Number(input.value) || 0;
          const minimum = Number(input.dataset.min) || 1;
          const increment = Number(input.dataset.increment) || 1;
          const next = Number(step.dataset.step) > 0 ? (current === 0 ? minimum : current + increment) : (current <= minimum ? 0 : current - increment);
          input.value = String(Math.max(0, Math.min(input.max ? Number(input.max) : Infinity, next)));
          this.updateTotals();
        }
      });
      this.addEventListener('input', event => {
        if (event.target.matches('input[type="number"]')) {
          this.clearToolUndo();
          event.target.setCustomValidity('');
          this.updateTotals();
        }
      });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.addItems(); });
      this.initSizeTools();
      this.renderSelection();
    }
    initSizeTools() {
      this.fillButton = this.querySelector('[data-fill-sizes]');
      if (!this.fillButton) return;
      this.undoButton = this.querySelector('[data-undo-sizes]');
      this.toolNote = this.querySelector('[data-size-tools-status]');
      this.copyToggle = this.querySelector('[data-copy-sizes]');
      this.copyPanel = this.querySelector('[data-copy-panel]');
      this.fillButton.addEventListener('click', () => {
        if (this.busy || this.requiresReview) return;
        const changes = this.currentSizeRows().flatMap(row => {
          const input = row.querySelector('.quantity__input');
          const value = Number(input.dataset.min) || 1;
          return this.toolQuantityAllowed(input, value) ? [{ input, value }] : [];
        });
        this.applyToolChanges(changes, 'Filled each available size. Review quantities before adding to cart.');
      });
      this.undoButton.addEventListener('click', () => {
        if (this.busy || this.requiresReview || !this.toolUndo) return;
        this.toolUndo.forEach(({ input, value }) => { input.value = value; input.setCustomValidity(''); });
        this.toolUndo = null;
        this.toolNote.textContent = 'Previous quantities restored.';
        this.updateTotals();
      });
      if (!this.copyToggle || !this.copyPanel) return;
      const id = 'ld-copy-colors-' + (this.constructor.toolSequence = (this.constructor.toolSequence || 0) + 1);
      this.copyPanel.id = id;
      this.copyToggle.setAttribute('aria-controls', id);
      this.copyApply = this.querySelector('[data-apply-copy]');
      this.copyChoices = this.querySelector('[data-copy-choices]');
      this.copyToggle.addEventListener('click', () => {
        if (this.busy || this.requiresReview) return;
        if (!this.copyPanel.hidden) { this.closeToolPanel(true); return; }
        try {
          this.readSizePattern();
          this.copyChoices.replaceChildren();
          this.destinationColors().forEach(color => {
            const label = document.createElement('label'); label.className = 'ld-bulk__copy-choice';
            const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.value = color;
            const text = document.createElement('span'); text.textContent = color;
            label.append(checkbox, text); this.copyChoices.appendChild(label);
          });
          this.toolNote.textContent = '';
          this.copyPanel.hidden = false;
          this.copyToggle.setAttribute('aria-expanded', 'true');
          this.updateSizeTools();
          this.copyChoices.querySelector('input')?.focus({ preventScroll: true });
        } catch (error) { this.toolNote.textContent = error.message; }
      });
      this.copyPanel.addEventListener('change', () => this.updateSizeTools());
      this.copyPanel.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input')) event.preventDefault();
      });
      this.copyPanel.addEventListener('keyup', event => {
        if (event.key === 'Escape') { event.stopPropagation(); this.closeToolPanel(true); }
      });
      this.querySelector('[data-cancel-copy]').addEventListener('click', () => {
        if (!this.busy) this.closeToolPanel(true);
      });
      this.copyApply.addEventListener('click', () => {
        if (this.busy || this.requiresReview) return;
        try {
          const colors = Array.from(this.copyChoices.querySelectorAll('input:checked'), input => input.value);
          if (!colors.length) return;
          const plan = this.planSizeCopy(colors);
          const message = 'Copied quantities to ' + plan.colorCount + (plan.colorCount === 1 ? ' color.' : ' colors.') +
            (plan.skipped ? ' ' + plan.skipped + ' unavailable or invalid size selections were skipped.' : '') +
            ' Review quantities before adding to cart.';
          if (!plan.changes.length) this.toolNote.textContent = 'No quantities could be copied. Check available sizes and quantity rules.';
          else this.applyToolChanges(plan.changes, message);
          this.closeToolPanel(true);
        } catch (error) { this.toolNote.textContent = error.message; }
      });
    }
    currentSizeRows() {
      return this.rows.filter(row => !row.hidden && row.querySelector('.quantity__input'));
    }
    toolQuantityAllowed(input, value) {
      const min = Number(input.dataset.min) || 1, step = Number(input.dataset.increment) || 1;
      return Number.isSafeInteger(value) && value >= 0 &&
        (value === 0 || (value >= min && (value - min) % step === 0)) &&
        (input.max === '' || value <= Number(input.max));
    }
    readSizePattern() {
      const pattern = new Map(), index = Number(this.dataset.sizeIndex);
      if (index < 0) throw new Error('This item has no size option.');
      this.currentSizeRows().forEach(row => {
        const input = row.querySelector('.quantity__input'), value = Number(input.value);
        if (input.value.trim() === '' || !this.toolQuantityAllowed(input, value)) throw new Error('Enter valid quantities for the current color first.');
        pattern.set(row.variantOptions[index], value);
      });
      if (!Array.from(pattern.values()).some(value => value > 0)) throw new Error('Enter quantities for the current color first.');
      return pattern;
    }
    destinationRows() {
      const colorIndex = Number(this.dataset.colorIndex);
      return this.rows.filter(row => Object.entries(this.selection).every(([index, value]) => Number(index) === colorIndex || row.variantOptions[Number(index)] === value));
    }
    destinationColors() {
      const index = Number(this.dataset.colorIndex);
      if (index < 0) return [];
      return [...new Set(this.destinationRows().filter(row => row.dataset.available === 'true' && row.querySelector('.quantity__input')).map(row => row.variantOptions[index]))]
        .filter(color => color !== this.selection[index]);
    }
    planSizeCopy(colors) {
      const pattern = this.readSizePattern(), changes = [];
      const sizeIndex = Number(this.dataset.sizeIndex), colorIndex = Number(this.dataset.colorIndex);
      const destinations = new Set(this.destinationColors());
      let skipped = 0, colorCount = 0;
      for (const color of new Set(colors)) {
        if (!destinations.has(color)) continue;
        const rows = this.destinationRows().filter(row => row.variantOptions[colorIndex] === color);
        const colorChanges = [];
        for (const [size, value] of pattern) {
          const row = rows.find(item => item.variantOptions[sizeIndex] === size);
          const input = row?.querySelector('.quantity__input');
          if (!input || row.dataset.available !== 'true' || !this.toolQuantityAllowed(input, value)) { if (value > 0) skipped++; continue; }
          colorChanges.push({ input, value });
        }
        // Do not erase a destination when none of the requested pieces can be copied.
        if (colorChanges.some(change => change.value > 0)) { changes.push(...colorChanges); colorCount++; }
      }
      return { changes, skipped, colorCount };
    }
    applyToolChanges(changes, message) {
      const changed = changes.filter(({ input, value }) => input.value !== String(value));
      if (!changed.length) { this.toolNote.textContent = 'No quantities changed.'; return; }
      this.toolUndo = changed.map(({ input }) => ({ input, value: input.value }));
      changed.forEach(({ input, value }) => { input.value = String(value); input.setCustomValidity(''); });
      this.toolNote.textContent = message;
      this.updateTotals();
    }
    clearToolUndo() {
      this.toolUndo = null;
      if (this.toolNote) this.toolNote.textContent = '';
      if (this.undoButton) this.undoButton.hidden = true;
    }
    closeToolPanel(returnFocus = false) {
      if (!this.copyPanel) return;
      this.copyPanel.hidden = true;
      this.copyToggle.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.copyToggle.focus({ preventScroll: true });
    }
    updateSizeTools() {
      if (!this.fillButton) return;
      const blocked = this.busy || this.requiresReview;
      const visible = this.currentSizeRows();
      this.fillButton.disabled = blocked || !visible.length;
      this.fillButton.textContent = visible.every(row => (Number(row.querySelector('.quantity__input').dataset.min) || 1) === 1) ? '1 Each' : 'Min Each';
      this.undoButton.hidden = !this.toolUndo;
      this.undoButton.disabled = Boolean(blocked);
      if (this.copyToggle) this.copyToggle.disabled = blocked || !this.destinationColors().length;
      if (this.copyApply) this.copyApply.disabled = blocked || !this.copyChoices.querySelector('input:checked');
    }

    format(cents) { return this.money.format(cents / 100); }
    renderSelection() {
      this.closeToolPanel();
      this.groups.forEach(group => {
        const chosen = this.selection[group.dataset.optionIndex];
        group.querySelector('[data-option-label]').textContent = chosen;
        group.querySelectorAll('[data-option-value]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.optionValue === chosen)));
      });
      const visible = this.rows.filter(row => {
        row.hidden = !Object.entries(this.selection).every(([index,value]) => row.variantOptions[Number(index)] === value);
        return !row.hidden;
      });
      const first = visible.find(row => row.dataset.available === 'true') || visible[0];
      if (first) {
        this.querySelector('[data-product-price]').textContent = this.format(Number(first.dataset.price));
        const compare = this.querySelector('[data-compare-price]');
        compare.hidden = Number(first.dataset.compare) <= Number(first.dataset.price);
        compare.textContent = this.format(Number(first.dataset.compare));
        const image = this.querySelector('.ld-bulk__image');
        if (image && first.dataset.image) { image.removeAttribute('srcset'); image.src = first.dataset.image; }
        if (this.classList.contains('ld-bulk__inline') && first.dataset.mediaId) {
          const info = this.closest('product-info');
          const gallery = info?.querySelector('media-gallery');
          const mediaKey = info?.dataset.section + '-' + first.dataset.mediaId;
          const media = gallery?.querySelector('[data-media-id="' + mediaKey + '"]');
          if (media && !media.classList.contains('is-active') && gallery.querySelector('[data-target="' + mediaKey + '"]')) gallery.setActiveMedia(mediaKey, false);
        }
      }
      this.updateTotals();
    }
    updateTotals() {
      let quantity = 0, price = 0;
      this.rows.forEach(row => {
        const input = row.querySelector('input');
        if (!input) return;
        const count = Math.max(0, Number(input.value) || 0);
        quantity += count;
        price += count * Number(row.dataset.price);
        const subtotal = row.querySelector('[data-row-subtotal]');
        if (subtotal) subtotal.textContent = this.format(count * Number(row.dataset.price));
        row.querySelector('[data-step="-1"]').disabled = this.busy || this.requiresReview || count === 0;
        row.querySelector('[data-step="1"]').disabled = this.busy || this.requiresReview || (input.max !== '' && count >= Number(input.max));
      });
      this.querySelector('[data-total-quantity]').textContent = String(quantity);
      this.querySelector('[data-total-price]').textContent = this.format(price);
      this.submitButton.disabled = this.busy || this.requiresReview || quantity === 0;
      this.updateSizeTools();
    }
    async addItems() {
      if (this.busy || this.requiresReview) return;
      const items = [];
      for (const row of this.rows) {
        const input = row.querySelector('input');
        if (!input) continue;
        input.setCustomValidity('');
        const quantity = Number(input.value);
        const minimum = Number(input.dataset.min) || 1;
        const increment = Number(input.dataset.increment) || 1;
        if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity > 0 && (quantity < minimum || (quantity - minimum) % increment !== 0 || (input.max !== '' && quantity > Number(input.max))))) {
          row.variantOptions.forEach((value,index) => { if (String(index) in this.selection) this.selection[index] = value; });
          this.renderSelection();
          input.setCustomValidity('Please enter a valid quantity for this size.');
          input.reportValidity();
          return;
        }
        if (quantity > 0) items.push({id:row.dataset.variantId, quantity});
      }
      if (!items.length) return;
      this.busy = true;
      this.updateTotals();
      this.querySelectorAll('input,[data-option-value]').forEach(input => input.disabled = true);
      this.submitButton.setAttribute('aria-busy','true');
      this.status.hidden = false;
      this.status.dataset.error = 'false';
      this.status.textContent = 'Adding to cart…';
      let uncertainAdd = false;
      try {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart?.getSectionsToRender?.().map(section => section.section || section.id) || ['cart-icon-bubble'];
        const purchaseModal = this.closest('quick-add-modal, .ld-bulk-modal');
        cart?.setActiveElement?.(purchaseModal?.openedBy || this.submitButton);
        uncertainAdd = true;
        const response = await fetch(ldThemeRequestURL(this.dataset.addUrl), {
          method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({items, sections, sections_url:ldCartSectionURL()})
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add these items. Please try again.');
        uncertainAdd = false;
        this.clearToolUndo();
        this.closeToolPanel();
        this.rows.forEach(row => { const input = row.querySelector('input'); if (input) input.value = '0'; });
        this.status.textContent = 'Added to cart.';
        this.querySelector('.ld-bulk__cart').hidden = false;
        try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source:'ld-bulk-order', cartData:result});
        if (cart?.renderContents && result.sections) {
          purchaseModal?.hide();
          cart.renderContents(result);
        } else if (result.sections?.['cart-icon-bubble']) {
          const html = new DOMParser().parseFromString(result.sections['cart-icon-bubble'],'text/html');
          const target = document.getElementById('cart-icon-bubble');
          const source = html.querySelector('.shopify-section');
          if (target && source) target.innerHTML = source.innerHTML;
        }
        } catch (renderError) { console.error('Cart display refresh failed', renderError); }
      } catch (error) {
        this.status.dataset.error = 'true';
        this.requiresReview = uncertainAdd;
        this.status.textContent = (error.message || 'Unable to add these items.') + (uncertainAdd ? ' Your cart may have changed. View your cart before adding again.' : ' Please try again.');
        if (uncertainAdd) this.querySelector('.ld-bulk__cart').hidden = false;
      } finally {
        this.busy = false;
        this.submitButton.removeAttribute('aria-busy');
        this.querySelectorAll('input,[data-option-value]').forEach(input => input.disabled = Boolean(this.requiresReview));
        this.updateTotals();
      }
    }
  });
}

// Reconcile both cart checkout areas after Shopify replaces cart sections.
if (!customElements.get('ld-wholesale-minimum')) {
  customElements.define('ld-wholesale-minimum', class extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const root = this.closest('cart-drawer, #main-cart-footer');
        if (!root) return;
        const blocked = this.dataset.blocked === 'true';
        const active = this.dataset.active === 'true';
        root.dataset.ldMinimumBlocked = String(blocked);
        root.querySelectorAll('button[name="checkout"]').forEach(button => {
          if (active || button.dataset.ldMinimumManaged === 'true') {
            button.disabled = blocked || this.dataset.empty === 'true';
            button.dataset.ldMinimumManaged = 'true';
          }
        });
      });
    }
  });
  document.addEventListener('submit', event => {
    if (!['cart', 'CartDrawer-Form'].includes(event.target.id)) return;
    const root = event.target.id === 'cart'
      ? document.getElementById('main-cart-footer')
      : event.target.closest('cart-drawer');
    const matrix = event.target.querySelector('ld-wholesale-cart-matrix');
    const pendingPanel = matrix && Array.from(matrix.querySelectorAll('[data-color-editor]')).find(panel => matrix.pending(panel).length);
    if (matrix && (matrix.busy || pendingPanel)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!matrix.busy && pendingPanel) matrix.saveColor(pendingPanel);
      return;
    }
    const guard = root?.querySelector('ld-wholesale-minimum');
    if (guard?.dataset.blocked === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      guard.scrollIntoView({ block: 'nearest' });
    }
  }, true);
}

// Keep wholesale checkout feedback consistent without changing native form submission.
(() => {
  let resetTimer;
  const reset = () => {
    clearTimeout(resetTimer);
    document.querySelectorAll('[data-ld-checkout-loading]').forEach(button => {
      button.classList.remove('ld-cart-action-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-ld-checkout-loading');
    });
  };
  document.addEventListener('click', event => {
    if (event.target.closest('[data-ld-checkout-loading]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', event => {
    if (event.defaultPrevented || !['cart', 'CartDrawer-Form'].includes(event.target.id)) return;
    const button = event.submitter;
    if (!button?.matches('button[name="checkout"]') || !button.closest('.ld-wholesale-cart-footer, cart-drawer.ld-wholesale-cart-drawer')) return;
    const root = button.closest('#main-cart-footer, cart-drawer');
    if (button.disabled || root?.dataset.ldMinimumBlocked === 'true') return;
    if (button.hasAttribute('data-ld-checkout-loading')) { event.preventDefault(); return; }
    button.dataset.ldCheckoutLoading = '';
    button.classList.add('ld-cart-action-loading');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    resetTimer = setTimeout(reset, 15000);
    queueMicrotask(() => { if (event.defaultPrevented) reset(); });
  });
  window.addEventListener('pageshow', reset);
})();

if (!customElements.get('ld-load-more')) {
  customElements.define('ld-load-more', class extends HTMLElement {
    connectedCallback() {
      if (this.onClick) return;
      this.onClick = event => {
        const link = event.target.closest('a[data-next]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault();
        this.autoLoad = true;
        this.loadNext(link, event.detail === 0);
      };
      this.addEventListener('click', this.onClick);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
    watchForMore() {
      this.observer?.disconnect();
      if (!this.isConnected || !this.autoLoad || !this.querySelector('a[data-next]')) return;
      this.observer = new IntersectionObserver(entries => {
        if (!this.autoLoad || !entries.some(entry => entry.isIntersecting)) return;
        const link = this.querySelector('a[data-next]');
        if (link) this.loadNext(link, false);
      }, { rootMargin: '0px 0px 300px 0px' });
      this.observer.observe(this);
    }
    async loadNext(link, keyboard) {
      if (this.loading) return;
      this.loading = true;
      this.controller = new AbortController();
      const status = this.querySelector('[data-load-status]');
      const label = link.textContent;
      link.textContent = 'Loading…';
      link.setAttribute('aria-disabled', 'true');
      this.setAttribute('aria-busy', 'true');
      status.textContent = this.autoLoad ? 'Loading…' : '';
      this.observer?.disconnect();
      try {
        const url = new URL(link.href, location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(ldThemeRequestURL(url), { signal: this.controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.querySelector('#product-grid');
        const grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
        const cardKey = item => item.querySelector('.card__heading a[href]')?.getAttribute('href')?.split('?')[0];
        const existing = new Set(Array.from(grid?.children || [], cardKey).filter(Boolean));
        const items = incoming ? Array.from(incoming.children).filter(el => {
          if (!el.matches('li.grid__item')) return false;
          const key = cardKey(el);
          if (key && existing.has(key)) return false;
          if (key) existing.add(key);
          return true;
        }) : [];
        if (!items.length || !grid) throw new Error('Product grid missing');
        if (!this.isConnected) return;
        const fragment = document.createDocumentFragment();
        const added = items.map(item => document.importNode(item, true));
        const loadedStylesheets = new Set(Array.from(document.querySelectorAll('link[rel="stylesheet"][href]'), el => el.href));
        added.forEach(item => {
          item.querySelectorAll('link[rel="stylesheet"][href]').forEach(stylesheet => {
            if (loadedStylesheets.has(stylesheet.href)) stylesheet.remove();
            else loadedStylesheets.add(stylesheet.href);
          });
          fragment.appendChild(item);
        });
        grid.appendChild(fragment);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          added.forEach(item => {
            item.classList.add('scroll-trigger--cancel');
            initializeScrollAnimationTrigger(item);
          });
        }
        const next = html.querySelector('ld-load-more a[data-next]');
        if (next && new URL(next.href).searchParams.get('page') !== url.searchParams.get('page')) {
          link.href = next.href;
          link.style.display = 'none';
          status.textContent = 'Scroll to load more.';
        } else {
          this.autoLoad = false;
          link.remove();
          status.textContent = 'You’ve reached the end.';
        }
        if (keyboard) added[0].querySelector('.card__heading a')?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.autoLoad = false;
          link.style.display = '';
          status.textContent = 'Couldn’t load products. Please try again.';
        }
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
        link.textContent = label;
        link.removeAttribute('aria-disabled');
        this.watchForMore();
      }
    }
  });
}

// Cart editing loads product options only when requested.
if (!customElements.get('ld-cart-editor')) {
  customElements.define('ld-cart-editor', class extends HTMLElement {
    static products = new Map();
    connectedCallback() {
      if (this.dialog) return;
      this.innerHTML = '<dialog class="ld-cart-editor" aria-labelledby="ld-cart-edit-title"><button type="button" class="ld-cart-editor__close" aria-label="Close">×</button><div class="ld-cart-editor__heading"><img alt="" width="64" height="88"><div><h2 id="ld-cart-edit-title">Edit Item</h2><p data-product-title></p></div></div><form><fieldset disabled><div data-options></div><label class="ld-cart-editor__field">Quantity<input name="quantity" type="number" min="1" step="1" required inputmode="numeric"></label></fieldset><p role="status" aria-live="polite" data-status></p><div class="ld-cart-editor__actions"><button type="button" class="button button--secondary" data-cancel>Cancel</button><button type="submit" class="button" data-save disabled>Save Changes</button></div><a class="link" data-review hidden>Review Cart</a></form></dialog>';
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-status]');
      this.saveButton = this.querySelector('[data-save]');
      this.quantity = this.querySelector('[name="quantity"]');
      this.querySelector('.ld-cart-editor__close').addEventListener('click', () => this.close());
      this.querySelector('[data-cancel]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.save(); });
      this.form.addEventListener('change', event => {
        if (event.target.matches('select')) this.selectVariant(Number(event.target.dataset.option));
      });
    }
    close() {
      if (this.busy) return;
      this.generation++;
      this.dialog.close();
      document.body.classList.remove('ld-cart-editing');
      this.opener?.focus();
    }
    async open(button) {
      this.opener = button;
      const generation = this.generation = (this.generation || 0) + 1;
      this.writeUncertain = false;
      this.lineKey = button.dataset.lineKey;
      this.originalVariant = Number(button.dataset.variantId);
      this.originalQuantity = Number(button.dataset.quantity);
      this.quantity.value = this.originalQuantity;
      this.querySelector('[data-options]').replaceChildren();
      this.querySelector('[data-product-title]').textContent = '';
      this.querySelector('img').hidden = true;
      this.querySelector('fieldset').disabled = true;
      this.saveButton.disabled = true;
      this.querySelector('[data-review]').hidden = true;
      this.status.textContent = 'Loading options…';
      document.body.classList.add('ld-cart-editing');
      this.dialog.showModal();
      try {
        const url = new URL(button.dataset.productUrl, location.origin);
        url.pathname += '.js';
        if (!this.constructor.products.has(url.href)) {
          const promise = fetch(url).then(async response => {
            if (!response.ok) throw new Error('Product options could not load. Please try again.');
            return response.json();
          }).catch(error => { this.constructor.products.delete(url.href); throw error; });
          this.constructor.products.set(url.href, promise);
        }
        const product = await this.constructor.products.get(url.href);
        if (generation !== this.generation || !this.dialog.open) return;
        this.product = product;
        const variant = product.variants.find(item => item.id === this.originalVariant);
        if (!variant) throw new Error('This item is no longer available. Please review your cart.');
        this.querySelector('[data-product-title]').textContent = product.title;
        const container = this.querySelector('[data-options]');
        product.options.forEach((option, index) => {
          if (product.variants.length === 1 && variant.title === 'Default Title') return;
          const label = document.createElement('label');
          label.className = 'ld-cart-editor__field';
          label.append(document.createTextNode(option.name));
          const select = document.createElement('select');
          select.dataset.option = index;
          select.setAttribute('aria-label', option.name);
          option.values.forEach(value => {
            const choice = document.createElement('option');
            choice.value = value;
            choice.textContent = value;
            choice.disabled = !product.variants.some(item => item.available && item.options[index] === value) && value !== variant.options[index];
            select.appendChild(choice);
          });
          select.value = variant.options[index];
          label.appendChild(select);
          container.appendChild(label);
        });
        this.selectedVariant = variant;
        this.querySelector('fieldset').disabled = false;
        this.updateVariant();
      } catch (error) { this.status.textContent = error.message; }
    }
    selectVariant(changedIndex) {
      const choices = Array.from(this.querySelectorAll('select')).map(select => select.value);
      let variant = this.product.variants.find(item => item.options.every((value, index) => value === choices[index]));
      if (!variant?.available) variant = this.product.variants.find(item => item.available && item.options[changedIndex] === choices[changedIndex]) || variant;
      this.selectedVariant = variant;
      if (variant) this.querySelectorAll('select').forEach((select, index) => { select.value = variant.options[index]; });
      this.updateVariant();
    }
    updateVariant() {
      const variant = this.selectedVariant;
      this.saveButton.disabled = !variant?.available;
      this.status.textContent = variant?.available ? '' : 'This option is sold out.';
      const image = this.querySelector('img');
      const src = variant?.featured_image?.src || this.product.featured_image;
      image.hidden = !src;
      if (src) { const url = new URL(src, location.origin); url.searchParams.set('width', '160'); image.src = url.href; }
    }
    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      if (body) this.writeUncertain = true;
      const response = await fetch(root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Could not save changes. Please try again.');
      if (body) this.writeUncertain = false;
      return result;
    }
    async save() {
      if (this.busy || !this.selectedVariant?.available || !this.form.reportValidity()) return;
      const quantity = Number(this.quantity.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;
      this.busy = true;
      this.querySelector('fieldset').disabled = true;
      this.saveButton.disabled = true;
      this.saveButton.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Saving changes…';
      let added = false;
      try {
        const cart = await this.request('cart.js');
        const original = cart.items.find(item => item.key === this.lineKey);
        if (!original || original.quantity !== this.originalQuantity) throw new Error('Your cart has changed. Close this window and try again.');
        if (this.selectedVariant.id === original.variant_id) {
          await this.request('cart/change.js', { id: original.key, quantity });
        } else {
          const replacement = { id: this.selectedVariant.id, quantity, properties: original.properties || {} };
          if (original.selling_plan_allocation) replacement.selling_plan = original.selling_plan_allocation.selling_plan.id;
          // Add first: an unavailable replacement never removes the original item.
          await this.request('cart/add.js', { items: [replacement] });
          added = true;
          const updated = await this.request('cart.js');
          let oldLine = updated.items.find(item => item.key === original.key);
          if (!oldLine) {
            const signature = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
            const matches = updated.items.filter(item => item.variant_id === original.variant_id && signature(item.properties) === signature(original.properties) && item.selling_plan_allocation?.selling_plan?.id === original.selling_plan_allocation?.selling_plan?.id);
            if (matches.length === 1) oldLine = matches[0];
          }
          if (!oldLine) throw new Error('Please review your cart to confirm the updated items.');
          await this.request('cart/change.js', { id: oldLine.key, quantity: 0 });
        }
        window.location.reload();
      } catch (error) {
        if (added || this.writeUncertain) {
          this.status.textContent = 'Your cart may have changed. Review your cart before trying again.';
          const review = this.querySelector('[data-review]');
          review.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
          review.hidden = false;
        } else {
          this.status.textContent = error.message;
          this.querySelector('fieldset').disabled = false;
          this.saveButton.disabled = false;
        }
      } finally {
        this.busy = false;
        this.saveButton.removeAttribute('aria-busy');
      }
    }
  });
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-cart-line-edit]');
    if (!button) return;
    let editor = document.querySelector('ld-cart-editor');
    if (!editor) { editor = document.createElement('ld-cart-editor'); document.body.appendChild(editor); }
    editor.open(button);
  });
}

// Wholesale cart presentation groups the existing Shopify lines without changing their identity.
if (!customElements.get('ld-wholesale-cart-matrix')) {
  customElements.define('ld-wholesale-cart-matrix', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.products = JSON.parse(this.querySelector('[data-matrix-products]').textContent);
        this.stabilizeOrder();
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
        this.content = this.querySelector('[data-matrix-content]');
        this.status = this.querySelector('[data-matrix-status]');
        this.render();
        this.dataset.ready = 'true';
        delete this.dataset.failed;
        clearTimeout(this.ldFallbackTimer);
      } catch (error) {
        clearTimeout(this.ldFallbackTimer);
        this.dataset.failed = 'true';
        const status = this.querySelector('[data-matrix-status]');
        if (status) status.textContent = 'The bulk layout could not load. Use the standard cart below.';
        console.error('Wholesale cart initialization failed', error);
        return;
      }
      this.addEventListener('input', event => {
        if (event.target.matches('[data-matrix-quantity]')) { event.target.setCustomValidity(''); event.target.classList.toggle('ld-empty-quantity', event.target.value === '0'); }
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-matrix-quantity]')) event.stopPropagation();
      });
      this.addEventListener('pointerdown', event => {
        const step = event.target.closest('[data-quantity-step]');
        if (step && step.closest('[data-quantity-control]').contains(document.activeElement)) event.preventDefault();
      });
      this.addEventListener('focusout', event => {
        if (this.busy) return;
        const control = event.target.closest('[data-quantity-control]');
        if (!control || control.contains(event.relatedTarget)) return;
        const input = control.querySelector('[data-matrix-quantity]'); if (!input) return;
        this.restoreFocusVariant = event.relatedTarget?.closest('[data-quantity-control]')?.querySelector('[data-matrix-quantity]')?.dataset.variant || null;
        this.saveColor(input.closest('[data-color-editor]'));
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-matrix-quantity]')) { event.preventDefault(); this.restoreFocusVariant = event.target.dataset.variant; this.saveColor(event.target.closest('[data-color-editor]')); }
      });
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-quantity-step]'); if (step) { this.stepQuantity(step); return; }
        const remove = event.target.closest('[data-remove-color]'); if (remove) this.removeColor(remove.dataset.removeColor);
      });
    }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    }
    stabilizeOrder() {
      const storageKey = 'ld-wholesale-cart-order-v1';
      let previous = [];
      try { const stored = JSON.parse(sessionStorage.getItem(storageKey) || '[]'); if (Array.isArray(stored)) previous = stored.filter(item => item && (typeof item.id === 'string' || typeof item.id === 'number')); } catch (_) {}
      const positions = new Map(previous.map((product, index) => [String(product.id), index]));
      this.products.sort((a, b) => (positions.get(String(a.id)) ?? Infinity) - (positions.get(String(b.id)) ?? Infinity));
      const order = this.products.map(product => {
        const sizeIndex = product.options.findIndex(name => /^(sizes?|尺码)$/i.test(name));
        const colorKey = line => JSON.stringify(line.options.filter((value, index) => index !== sizeIndex));
        const saved = previous.find(item => String(item.id) === String(product.id));
        const colors = new Map((Array.isArray(saved?.colors) ? saved.colors : []).map((key, index) => [key, index]));
        product.lines.sort((a, b) => (colors.get(colorKey(a)) ?? Infinity) - (colors.get(colorKey(b)) ?? Infinity));
        return { id: product.id, colors: [...new Set(product.lines.map(colorKey))] };
      });
      try { sessionStorage.setItem(storageKey, JSON.stringify(order)); } catch (_) {}
    }
    format(cents) { return this.money.format(cents / 100); }
    render() {
      this.colorGroups = new Map();
      const cartRoot = this.closest('cart-items');
      const main = document.getElementById('MainContent');
      if (cartRoot && main) main.style.setProperty('--ld-cart-surface', getComputedStyle(cartRoot).getPropertyValue('--color-background'));
      const pieces = this.products.reduce((sum, product) => sum + product.lines.reduce((count, line) => count + line.quantity, 0), 0);
      this.content.appendChild(this.element('p', 'ld-product-cart-count', this.products.length + (this.products.length === 1 ? ' style' : ' styles') + ' / ' + pieces + (pieces === 1 ? ' piece' : ' pieces')));
      const searchLabel = this.element('label', 'ld-order-search');
      const search = this.element('input'); search.type = 'search'; search.placeholder = 'Search by product or SKU'; search.setAttribute('aria-label', 'Search cart by product, SKU or color');
      searchLabel.appendChild(search); this.content.appendChild(searchLabel);
      const empty = this.element('p', 'ld-order-no-results', 'No matching products in your cart.'); empty.hidden = true; empty.setAttribute('role', 'status'); this.content.appendChild(empty);
      search.addEventListener('keydown', event => { if (event.key === 'Enter') event.preventDefault(); });
      search.addEventListener('input', () => {
        const active = Array.from(this.querySelectorAll('[data-color-editor]')).find(panel => this.pending(panel).length);
        if (active && this.pending(active).length) { search.value = this.lastSearch || ''; this.status.textContent = 'Finish updating the quantity before searching.'; return; }
        this.lastSearch = search.value;
        this.filterProducts(search.value);
      });
      this.products.forEach(product => {
        const group = this.element('section', 'ld-order-product'); group.dataset.orderProduct = ''; group.dataset.colorEditor = ''; group.id = 'ld-order-product-' + product.id;
        group.setAttribute('aria-label', product.title); group.dataset.search = [product.title, product.sku, ...product.lines.flatMap(line => line.options)].join(' ').toLocaleLowerCase();
        const lead = this.element('a', 'ld-order-product__image'); lead.href = product.url;
        if (product.image) { const image = this.element('img'); image.src = product.image; image.alt = product.title; image.width = 88; image.height = 120; image.loading = 'lazy'; lead.appendChild(image); }
        group.appendChild(lead);
        const body = this.element('div', 'ld-order-product__body'); group.appendChild(body);
        const header = this.element('div', 'ld-order-product__header');
        const heading = this.element('div', 'ld-order-product__heading');
        const title = this.element('a', 'ld-order-product__title', product.title); title.href = product.url; heading.appendChild(title);
        if (product.sku) heading.appendChild(this.element('p', 'ld-order-product__sku', 'SKU: ' + product.sku));
        header.appendChild(heading);
        const prices = this.element('div', 'ld-order-product__price');
        const range = values => { const min = Math.min(...values), max = Math.max(...values); return this.format(min) + (max > min ? '–' + this.format(max) : ''); };
        const original = product.lines.map(line => Math.round(line.original / line.quantity));
        const final = product.lines.map(line => Math.round(line.final / line.quantity));
        if (product.lines.some(line => line.original > line.final)) prices.appendChild(this.element('s', '', range(original)));
        prices.appendChild(this.element('span', '', range(final)));
        prices.appendChild(this.element('small', '', 'per unit')); header.appendChild(prices);
        const productStatus = this.element('span', 'ld-order-product__status'); productStatus.setAttribute('role', 'status'); productStatus.setAttribute('aria-live', 'polite'); header.appendChild(productStatus);
        body.appendChild(header);
        const sizeIndex = product.options.findIndex(name => /^(sizes?|尺码)$/i.test(name));
        const sizeOf = variant => sizeIndex < 0 ? 'One size' : variant.options[sizeIndex];
        const colorKey = variant => JSON.stringify(variant.options.filter((value, index) => index !== sizeIndex));
        const sizes = [...new Set(product.variants.map(sizeOf))];
        const colors = new Map(); product.lines.forEach(line => { const key = colorKey(line); if (!colors.has(key)) colors.set(key, line); });
        const wrap = this.element('div', 'ld-order-table-wrap'); wrap.id = group.id + '-table';
        const table = this.element('table', 'ld-order-table'); table.setAttribute('aria-label', 'Sizes and quantities for ' + product.title);
        const thead = this.element('thead'); const headings = this.element('tr');
        ['Color', ...sizes, 'Qty', 'Subtotal', ''].forEach((label, index) => { const th = this.element('th', index === 0 ? 'ld-order-table__color' : index === sizes.length + 2 ? 'ld-order-table__subtotal' : index === sizes.length + 3 ? 'ld-order-table__remove' : '', label); th.scope = 'col'; if (index === sizes.length + 3) th.setAttribute('aria-label', 'Remove Color'); headings.appendChild(th); });
        thead.appendChild(headings); table.appendChild(thead);
        const tbody = this.element('tbody'); let colorIndex = 0;
        colors.forEach((first, key) => {
          const lines = product.lines.filter(line => colorKey(line) === key);
          const id = 'ld-color-' + product.id + '-' + colorIndex++; this.colorGroups.set(id, lines);
          const color = first.options.filter((value, index) => index !== sizeIndex && value !== 'Default Title').join(' / ') || 'Default';
          const row = this.element('tr'); row.dataset.colorRow = id;
          const colorCell = this.element('th', 'ld-order-table__color'); colorCell.scope = 'row';
          const colorInfo = this.element('div', 'ld-order-color');
          const src = first.image || product.image;
          if (src) { const image = this.element('img'); image.src = src; image.alt = color; image.width = 32; image.height = 46; image.loading = 'lazy'; colorInfo.appendChild(image); }
          colorInfo.appendChild(this.element('span', '', color)); colorCell.appendChild(colorInfo); row.appendChild(colorCell);
          sizes.forEach(size => {
            const cell = this.element('td', 'ld-order-table__size'); cell.dataset.size = size;
            const variant = product.variants.find(item => colorKey(item) === key && sizeOf(item) === size);
            const matches = variant ? lines.filter(line => line.id === variant.id) : [], line = matches[0];
            const current = matches.reduce((sum, item) => sum + item.quantity, 0);
            if (variant) {
              const input = this.element('input'); input.type = 'number'; input.inputMode = 'numeric'; input.min = '0'; input.step = '1'; input.value = current; input.dataset.previous = current; input.dataset.matrixQuantity = ''; input.dataset.variant = variant.id; input.dataset.key = line?.key || ''; input.dataset.minimum = variant.min || 1; input.dataset.increment = variant.step || 1; input.classList.toggle('ld-empty-quantity', current === 0);
              if (variant.max != null) input.max = variant.max;
              if (!variant.available) input.max = current;
              input.disabled = (!variant.available && !current) || matches.length > 1 || (!line && lines.some(item => item.special));
              input.setAttribute('aria-label', 'Quantity for ' + product.title + ' / ' + color + ' / ' + size);
              if (!variant.available && !current) input.title = 'Sold out';
              cell.appendChild(this.quantityControl(input));
              if (matches.some(item => item.special) || matches.length > 1) matches.forEach(item => {
                const options = this.element('button', 'link ld-order-options', 'Options'); options.type = 'button'; options.dataset.cartLineEdit = ''; options.dataset.lineKey = item.key; options.dataset.productUrl = product.url; options.dataset.variantId = item.id; options.dataset.quantity = item.quantity; options.setAttribute('aria-haspopup', 'dialog'); options.setAttribute('aria-label', 'Change options for ' + product.title + ' / ' + color + ' / ' + size); cell.appendChild(options);
              });
            }
            if (!variant) cell.appendChild(this.element('span', 'ld-order-unavailable', '—'));
            row.appendChild(cell);
          });
          const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
          const units = this.element('td', 'ld-order-table__units', String(quantity)); units.dataset.label = 'Qty'; row.appendChild(units);
          const subtotal = this.element('td', 'ld-order-table__subtotal', this.format(lines.reduce((sum, line) => sum + line.final, 0))); subtotal.dataset.label = 'Subtotal'; row.appendChild(subtotal);
          const removeCell = this.element('td', 'ld-order-table__remove');
          const remove = this.element('button', 'ld-order-remove', '×'); remove.type = 'button'; remove.dataset.removeColor = id; remove.setAttribute('aria-label', 'Remove ' + product.title + ' / ' + color); remove.title = 'Remove Color'; removeCell.appendChild(remove); row.appendChild(removeCell);
          tbody.appendChild(row);
        });
        table.appendChild(tbody); wrap.appendChild(table); body.appendChild(wrap);
        this.addColorPicker(product, body, colors, sizeIndex, sizes, colorKey, sizeOf);
        this.content.appendChild(group);
      });
    }
    quantityControl(input) {
      const control = this.element('div', 'ld-qty-control'); control.dataset.quantityControl = '';
      const minus = this.element('button', 'ld-qty-control__button', '−'); minus.type = 'button'; minus.dataset.quantityStep = '-1';
      const plus = this.element('button', 'ld-qty-control__button', '+'); plus.type = 'button'; plus.dataset.quantityStep = '1';
      control.append(minus, input, plus);
      input.addEventListener('focus', () => input.select());
      input.addEventListener('click', () => input.select());
      input.addEventListener('input', () => this.syncQuantityControl(input));
      this.syncQuantityControl(input);
      return control;
    }
    nextQuantity(input, direction) {
      let value = Number(input.value);
      if (!Number.isFinite(value) || input.value.trim() === '') value = Number(input.dataset.previous) || 0;
      const min = Number(input.dataset.minimum) || 1, step = Number(input.dataset.increment) || 1;
      if (direction > 0) return value < min ? min : min + (Math.floor((value - min) / step) + 1) * step;
      return value <= min ? 0 : Math.max(0, min + (Math.ceil((value - min) / step) - 1) * step);
    }
    syncQuantityControl(input) {
      const control = input.closest('[data-quantity-control]'); if (!control) return;
      const phrase = input.getAttribute('aria-label') || 'Quantity';
      control.querySelectorAll('[data-quantity-step]').forEach(button => {
        const direction = Number(button.dataset.quantityStep);
        button.setAttribute('aria-label', (direction > 0 ? 'Increase ' : 'Decrease ') + phrase.charAt(0).toLowerCase() + phrase.slice(1));
        const next = this.nextQuantity(input, direction);
        button.disabled = input.disabled || (direction < 0 && Number(input.value) <= 0) || (direction > 0 && input.max !== '' && next > Number(input.max));
      });
    }
    stepQuantity(button) {
      if (this.busy || button.disabled) return;
      const input = button.closest('[data-quantity-control]').querySelector('input');
      if (input.disabled) return;
      input.value = String(this.nextQuantity(input, Number(button.dataset.quantityStep)));
      input.setCustomValidity(''); input.dispatchEvent(new Event('input', { bubbles: true }));
      if (input.matches('[data-matrix-quantity]')) {
        this.restoreFocusVariant = input.dataset.variant;
        clearTimeout(this.quantityTimer);
        const panel = input.closest('[data-color-editor]');
        panel.querySelector('.ld-order-product__status').textContent = '';
        this.quantityTimer = setTimeout(() => this.saveColor(panel), 280);
      }
    }
    addColorPicker(product, body, currentColors, sizeIndex, sizes, colorKey, sizeOf) {
      if (!product.options.some(name => /^colou?rs?$/i.test(name)) || product.lines.some(line => line.special)) return;
      const choices = new Map();
      product.variants.forEach(variant => { const key = colorKey(variant); if (variant.available && !currentColors.has(key) && !choices.has(key)) choices.set(key, variant); });
      if (!choices.size) return;
      const footer = this.element('div', 'ld-order-more-colors');
      const toggle = this.element('button', 'link ld-order-more-colors__toggle', '+ Add More Colors'); toggle.type = 'button'; toggle.setAttribute('aria-expanded', 'false'); toggle.setAttribute('aria-controls', 'ld-more-colors-' + product.id); toggle.setAttribute('aria-label', 'Add more colors for ' + product.title);
      footer.appendChild(toggle); body.appendChild(footer);
      const row = this.element('tr', 'ld-order-new-color'); row.id = 'ld-more-colors-' + product.id; row.hidden = true;
      row.addEventListener('change', event => event.stopPropagation());
      const colorCell = this.element('th', 'ld-order-table__color'); colorCell.scope = 'row';
      const colorInfo = this.element('div', 'ld-order-color');
      const preview = this.element('img'); preview.width = 32; preview.height = 46; preview.hidden = true; preview.loading = 'lazy';
      const select = this.element('select'); select.setAttribute('aria-label', 'Additional color for ' + product.title);
      const placeholder = this.element('option', '', 'Choose Color'); placeholder.value = ''; select.appendChild(placeholder);
      choices.forEach((variant, key) => { const option = this.element('option', '', variant.options.filter((value, index) => index !== sizeIndex && value !== 'Default Title').join(' / ')); option.value = key; select.appendChild(option); });
      select.hidden = true; select.setAttribute('aria-hidden', 'true'); select.tabIndex = -1;
      const picker = this.element('button', 'ld-order-color-picker'); picker.type = 'button'; picker.setAttribute('role', 'combobox'); picker.setAttribute('aria-haspopup', 'listbox'); picker.setAttribute('aria-expanded', 'false'); picker.setAttribute('aria-label', 'Additional color for ' + product.title);
      const pickerText = this.element('span', '', 'Choose Color'); picker.appendChild(pickerText);
      const popup = this.element('div', 'ld-color-popover'); popup.id = 'ld-color-options-' + product.id; popup.setAttribute('popover', 'auto'); popup.setAttribute('role', 'listbox'); popup.setAttribute('aria-label', 'Available colors for ' + product.title); picker.setAttribute('aria-controls', popup.id);
      const optionButtons = [];
      choices.forEach((variant, key) => {
        const option = this.element('button', 'ld-color-popover__option'); option.type = 'button'; option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false'); option.dataset.colorKey = key;
        const image = this.element('img'); image.width = 28; image.height = 38; image.alt = ''; image.hidden = true; image.loading = 'lazy';
        option.append(image, this.element('span', '', variant.options.filter((value, index) => index !== sizeIndex && value !== 'Default Title').join(' / '))); popup.appendChild(option); optionButtons.push(option);
        option.addEventListener('click', () => {
          if (this.busy) return;
          select.value = key; pickerText.textContent = option.querySelector('span').textContent;
          optionButtons.forEach(button => button.setAttribute('aria-selected', String(button === option)));
          select.dispatchEvent(new Event('change', { bubbles: true })); popup.hidePopover(); picker.setAttribute('aria-expanded', 'false'); picker.focus({ preventScroll: true });
        });
      });
      let popupEvents;
      const closePopup = () => { if (popup.matches(':popover-open')) popup.hidePopover(); picker.setAttribute('aria-expanded', 'false'); };
      const positionPopup = () => {
        const anchor = picker.getBoundingClientRect(), height = popup.getBoundingClientRect().height, width = Math.min(248, document.documentElement.clientWidth - 24);
        popup.style.width = width + 'px'; popup.style.left = Math.min(Math.max(12, anchor.left), document.documentElement.clientWidth - width - 12) + 'px';
        popup.style.top = (anchor.bottom + height + 8 <= innerHeight - 12 ? anchor.bottom + 6 : Math.max(12, anchor.top - height - 6)) + 'px';
      };
      const openPopup = (last, toggle = false) => {
        if (this.busy) return;
        if (popup.matches(':popover-open')) { if (toggle) closePopup(); else (optionButtons.find(button => button.getAttribute('aria-selected') === 'true') || optionButtons[0])?.focus({ preventScroll: true }); return; }
        popup.showPopover();
        positionPopup();
        picker.setAttribute('aria-expanded', 'true');
        (optionButtons.find(button => button.getAttribute('aria-selected') === 'true') || (last ? optionButtons[optionButtons.length - 1] : optionButtons[0]))?.focus({ preventScroll: true });
        popupEvents = new AbortController(); if (!this.colorPopupEvents) this.colorPopupEvents = new Set(); this.colorPopupEvents.add(popupEvents);
        window.addEventListener('resize', closePopup, { signal: popupEvents.signal });
        window.addEventListener('scroll', event => { if (!popup.contains(event.target)) { const rect = picker.getBoundingClientRect(); if (rect.bottom < 0 || rect.top > innerHeight) closePopup(); else positionPopup(); } }, { capture: true, signal: popupEvents.signal });
      };
      picker.addEventListener('click', () => openPopup(false, true));
      picker.addEventListener('keydown', event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); openPopup(event.key === 'ArrowUp'); } });
      popup.addEventListener('toggle', event => { picker.setAttribute('aria-expanded', String(event.newState === 'open')); if (event.newState === 'closed' && popupEvents) { popupEvents.abort(); this.colorPopupEvents?.delete(popupEvents); popupEvents = null; } });
      popup.addEventListener('keydown', event => {
        const index = optionButtons.indexOf(document.activeElement);
        if (event.key === 'Tab') { closePopup(); picker.focus({ preventScroll: true }); }
        else if (event.key === 'Escape') { event.preventDefault(); closePopup(); picker.focus({ preventScroll: true }); }
        else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); const next = event.key === 'Home' ? 0 : event.key === 'End' ? optionButtons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + optionButtons.length) % optionButtons.length; optionButtons[next]?.focus();
        } else if (event.key.length === 1 && /[a-z]/i.test(event.key)) { optionButtons.find(button => button.textContent.trim().toLocaleLowerCase().startsWith(event.key.toLocaleLowerCase()))?.focus(); }
      });
      colorInfo.append(preview, picker, select, popup); colorCell.appendChild(colorInfo); row.appendChild(colorCell);
      const inputs = [];
      sizes.forEach(size => {
        const cell = this.element('td', 'ld-order-table__size'); cell.dataset.size = size;
        const input = this.element('input', 'ld-empty-quantity'); input.type = 'number'; input.inputMode = 'numeric'; input.min = '0'; input.step = '1'; input.value = '0'; input.dataset.newColorQuantity = ''; input.dataset.size = size; input.disabled = true;
        cell.appendChild(this.quantityControl(input)); row.appendChild(cell); inputs.push(input);
      });
      const units = this.element('td', 'ld-order-table__units', '0'); units.dataset.label = 'Qty'; row.appendChild(units);
      const addCell = this.element('td', 'ld-order-table__subtotal');
      const add = this.element('button', 'button ld-order-new-color__add', 'Add Color'); add.type = 'button'; add.setAttribute('aria-label', 'Add selected color for ' + product.title); add.disabled = true; addCell.appendChild(add); row.appendChild(addCell);
      const cancelCell = this.element('td', 'ld-order-table__remove');
      const cancel = this.element('button', 'ld-order-remove', '×'); cancel.type = 'button'; cancel.title = 'Cancel'; cancel.setAttribute('aria-label', 'Cancel adding colors for ' + product.title); cancelCell.appendChild(cancel); row.appendChild(cancelCell);
      body.querySelector('.ld-order-table tbody').appendChild(row);
      const notice = this.element('p', 'ld-order-more-colors__notice'); notice.setAttribute('role', 'status'); notice.setAttribute('aria-live', 'polite'); body.appendChild(notice);
      let details;
      const setImage = () => {
        const variant = choices.get(select.value);
        const src = variant ? details?.variants?.find(item => item.id === variant.id)?.featured_image?.src : null;
        optionButtons.forEach(button => {
          const colorVariant = choices.get(button.dataset.colorKey), image = button.querySelector('img');
          const imageSrc = details?.variants?.find(item => item.id === colorVariant.id)?.featured_image?.src;
          image.hidden = !imageSrc;
          if (imageSrc) { const imageUrl = new URL(imageSrc, location.origin); imageUrl.searchParams.set('width', '80'); image.src = imageUrl.href; }
        });
        preview.hidden = !src;
        if (src) { const url = new URL(src, location.origin); url.searchParams.set('width', '120'); preview.src = url.href; preview.alt = select.options[select.selectedIndex].text; }
      };
      toggle.addEventListener('click', () => {
        if (this.busy) return;
        row.hidden = false; footer.hidden = true; toggle.setAttribute('aria-expanded', 'true'); picker.focus({ preventScroll: true });
        if (!this.constructor.colorDetails) this.constructor.colorDetails = new Map();
        const cache = this.constructor.colorDetails;
        if (!cache.has(product.url)) {
          const url = new URL(product.url, location.origin); url.pathname = url.pathname.replace(/\/$/, '') + '.js'; url.search = '';
          cache.set(product.url, fetch(url.href, { headers: { Accept: 'application/json' } }).then(response => { if (!response.ok) throw new Error('Image unavailable'); return response.json(); }).catch(error => { cache.delete(product.url); throw error; }));
        }
        cache.get(product.url).then(data => { details = data; if (this.isConnected) setImage(); }).catch(() => {});
      });
      cancel.addEventListener('click', () => { if (!this.busy) { row.hidden = true; footer.hidden = false; notice.textContent = ''; closePopup(); select.value = ''; pickerText.textContent = 'Choose Color'; optionButtons.forEach(button => button.setAttribute('aria-selected', 'false')); preview.hidden = true; add.disabled = true; units.textContent = '0'; inputs.forEach(input => { input.value = '0'; input.disabled = true; input.setCustomValidity(''); input.classList.add('ld-empty-quantity'); this.syncQuantityControl(input); }); toggle.setAttribute('aria-expanded', 'false'); toggle.focus({ preventScroll: true }); } });
      select.addEventListener('change', () => {
        notice.textContent = ''; add.disabled = !select.value; setImage(); units.textContent = '0';
        inputs.forEach(input => {
          const variant = select.value ? product.variants.find(item => colorKey(item) === select.value && sizeOf(item) === input.dataset.size) : null;
          input.value = '0'; input.setCustomValidity(''); input.classList.add('ld-empty-quantity'); input.disabled = !variant?.available; input.removeAttribute('max'); input.title = '';
          input.dataset.variant = variant?.id || ''; input.dataset.minimum = variant?.min || 1; input.dataset.increment = variant?.step || 1;
          if (variant?.max != null) input.max = variant.max;
          if (select.value && input.disabled) input.title = 'Unavailable';
          input.setAttribute('aria-label', 'Quantity to add for ' + product.title + ' / ' + (select.value ? select.options[select.selectedIndex].text : 'new color') + ' / ' + input.dataset.size); this.syncQuantityControl(input);
        });
      });
      inputs.forEach(input => input.addEventListener('input', () => {
        input.setCustomValidity(''); input.classList.toggle('ld-empty-quantity', input.value === '0'); notice.textContent = '';
        units.textContent = String(inputs.reduce((sum, control) => sum + (control.disabled ? 0 : Math.max(0, Number(control.value) || 0)), 0));
      }));
      const addSelected = async () => {
        if (this.busy || !select.value) return;
        const items = [];
        for (const input of inputs.filter(control => !control.disabled)) {
          const value = Number(input.value), min = Number(input.dataset.minimum), step = Number(input.dataset.increment);
          if (input.value.trim() === '' || !Number.isSafeInteger(value) || value < 0 || (value > 0 && (value < min || (value - min) % step !== 0)) || (input.max !== '' && value > Number(input.max))) {
            input.setCustomValidity('Enter 0, or a valid quantity for this size.'); input.reportValidity(); return;
          }
          if (value > 0) items.push({ id: Number(input.dataset.variant), quantity: value });
        }
        if (!items.length) { notice.textContent = 'Enter a quantity for at least one size.'; inputs.find(input => !input.disabled)?.focus(); return; }
        notice.textContent = ''; this.restoreFocusVariant = String(items[0].id);
        await this.mutate('cart/add.js', { items }, null, add);
        if (row.isConnected) notice.textContent = this.status.textContent || 'Could not add this color. Please try again.';
      };
      add.addEventListener('click', addSelected);
      row.addEventListener('keydown', event => { if (event.key === 'Enter' && event.target.matches('[data-new-color-quantity]')) { event.preventDefault(); addSelected(); } });
    }
    filterProducts(value) {
      const query = value.trim().toLocaleLowerCase(); let matches = 0;
      this.querySelectorAll('[data-order-product]').forEach(group => { group.hidden = !group.dataset.search.includes(query); if (!group.hidden) matches++; });
      this.querySelector('.ld-order-no-results').hidden = matches > 0;
    }
    pending(panel) { return Array.from(panel.querySelectorAll('[data-matrix-quantity]')).filter(input => input.value.trim() === '' || Number(input.value) !== Number(input.dataset.previous)); }
    saveColor(panel) {
      clearTimeout(this.quantityTimer);
      if (this.busy || !this.isConnected) return;
      const changed = this.pending(panel);
      if (!changed.length) { panel.querySelector('.ld-order-product__status').textContent = ''; return; }
      if (changed.some(input => input.value.trim() === '')) {
        changed.filter(input => input.value.trim() === '').forEach(input => { input.value = input.dataset.previous; input.classList.toggle('ld-empty-quantity', input.value === '0'); });
        panel.querySelector('.ld-order-product__status').textContent = 'Quantity unchanged. Enter 0 to remove a size.';
        return;
      }
      this.changeQuantity(changed[0]);
    }
    async removeColor(id) {
      if (this.busy) return;
      const active = Array.from(this.querySelectorAll('[data-color-editor]')).find(panel => this.pending(panel).length);
      if (active && this.pending(active).length) { this.status.textContent = 'Finish updating the quantity first.'; return; }
      const updates = Object.fromEntries(this.colorGroups.get(id).map(line => [line.key, 0]));
      const removeButton = Array.from(this.querySelectorAll('[data-remove-color]')).find(button => button.dataset.removeColor === id);
      await this.mutate('cart/update.js', { updates }, null, removeButton);
    }
    disconnectedCallback() { clearTimeout(this.ldFallbackTimer); clearTimeout(this.quantityTimer); this.colorPopupEvents?.forEach(controller => controller.abort()); }
    async changeQuantity(input) {
      if (!this.isConnected) return;
      if (this.busy) { input.value = input.dataset.previous; return; }
      if (input.value.trim() === '') { input.value = input.dataset.previous; return; }
      const quantity = Number(input.value);
      const minimum = Number(input.dataset.minimum) || 1;
      const increment = Number(input.dataset.increment) || 1;
      if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity > 0 && (quantity < minimum || (quantity - minimum) % increment !== 0)) || (input.max !== '' && quantity > Number(input.max))) {
        input.setCustomValidity('Enter 0 to remove, or a valid quantity for this size.'); input.reportValidity(); input.value = input.dataset.previous; input.addEventListener('input', () => input.setCustomValidity(''), { once: true }); return;
      }
      input.setCustomValidity('');
      if (quantity === Number(input.dataset.previous)) return;
      const changed = Array.from((input.closest('[data-color-editor]') || this).querySelectorAll('[data-matrix-quantity]')).filter(control => control.value.trim() === '' || Number(control.value) !== Number(control.dataset.previous));
      const operations = [];
      const additions = [];
      for (const control of changed) {
        const value = Number(control.value), min = Number(control.dataset.minimum) || 1, step = Number(control.dataset.increment) || 1;
        if (control.value.trim() === '' || !Number.isSafeInteger(value) || value < 0 || (value > 0 && (value < min || (value - min) % step !== 0)) || (control.max !== '' && value > Number(control.max))) {
          control.setCustomValidity('Enter 0 to remove, or a valid quantity for this size.'); control.reportValidity(); return;
        }
        if (control.dataset.key) operations.push({ path: 'cart/change.js', payload: { id: control.dataset.key, quantity: value } });
        else if (value > 0) additions.push({ id: Number(control.dataset.variant), quantity: value });
      }
      if (additions.length) operations.push({ path: 'cart/add.js', payload: { items: additions } });
      if (operations.length) await this.mutate(operations, {}, input);
    }
    async mutate(path, payload, input, actionButton) {
      if (this.busy) return;
      this.busy = true;
      this.status.textContent = '';
      const focusVariant = this.restoreFocusVariant, searchValue = this.lastSearch || ''; this.restoreFocusVariant = null;
      const productStatus = (input || actionButton)?.closest('[data-color-editor]')?.querySelector('.ld-order-product__status');
      if (productStatus) productStatus.textContent = '';
      const loadingButton = actionButton || input?.closest('tr')?.querySelector('[data-remove-color]');
      const loadingLabel = loadingButton?.getAttribute('aria-label');
      if (loadingButton) {
        loadingButton.setAttribute('aria-busy', 'true');
        loadingButton.classList.toggle('ld-cart-action-loading', !loadingButton.matches('[data-remove-color]'));
        loadingButton.setAttribute('aria-label', actionButton ? (actionButton.matches('[data-remove-color]') ? 'Removing color' : 'Adding color') : 'Saving quantity');
      }
      const checkout = Array.from(document.querySelectorAll('#main-cart-footer [name="checkout"]'));
      const checkoutDisabled = checkout.map(button => button.disabled);
      checkout.forEach(button => { button.disabled = true; });
      const controls = Array.from(this.querySelectorAll('input,button,select'));
      const disabled = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      let updated = false;
      let uncertain = false;
      try {
        const itemsRoot = document.getElementById('main-cart-items');
        const footerRoot = document.getElementById('main-cart-footer');
        const sections = [itemsRoot.dataset.id, footerRoot.dataset.id, 'cart-icon-bubble'];
        const operations = Array.isArray(path) ? path : [{ path, payload }];
        let result;
        for (let index = 0; index < operations.length; index++) {
          const operation = operations[index];
          const body = index === operations.length - 1 ? { ...operation.payload, sections, sections_url: ldCartSectionURL() } : operation.payload;
          uncertain = true;
          const response = await fetch(ldThemeRequestURL((window.Shopify?.routes?.root || '/') + operation.path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
          result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Could not update cart. Please try again.');
          updated = true;
          uncertain = false;
        }
        const parse = html => new DOMParser().parseFromString(html, 'text/html');
        const freshItems = parse(result.sections?.[sections[0]] || '');
        const freshFooter = parse(result.sections?.[sections[1]] || '').getElementById('main-cart-footer');
        const freshContents = freshItems.querySelector('#main-cart-items .js-contents');
        if (!freshContents || !freshFooter) throw new Error('Cart updated. Reload the page to view your changes.');
        const scrollAnchor = Array.from(this.querySelectorAll('[data-order-product]')).find(group => !group.hidden && group.getBoundingClientRect().bottom > 140);
        const anchorId = scrollAnchor?.id, anchorTop = scrollAnchor?.getBoundingClientRect().top;
        const scrollTop = window.scrollY;
        const cartItems = this.closest('cart-items');
        cartItems.classList.toggle('is-empty', freshItems.querySelector('cart-items')?.classList.contains('is-empty'));
        const currentNote = footerRoot.querySelector('cart-note');
        const incomingNote = freshFooter.querySelector('cart-note');
        if (currentNote && incomingNote) incomingNote.replaceWith(currentNote);
        const currentNoteDetails = footerRoot.querySelector('.ld-order-note');
        const incomingNoteDetails = freshFooter.querySelector('.ld-order-note');
        if (currentNoteDetails && incomingNoteDetails) incomingNoteDetails.open = currentNoteDetails.open;
        footerRoot.replaceWith(freshFooter);
        const bubble = parse(result.sections?.['cart-icon-bubble'] || '').querySelector('.shopify-section');
        if (bubble) document.getElementById('cart-icon-bubble')?.replaceChildren(...bubble.childNodes);
        itemsRoot.querySelector('.js-contents').replaceChildren(...freshContents.childNodes);
        const freshCart = itemsRoot.querySelector('ld-wholesale-cart-matrix');
        if (searchValue && freshCart) { freshCart.lastSearch = searchValue; freshCart.querySelector('.ld-order-search input').value = searchValue; freshCart.filterProducts(searchValue); }
        if (focusVariant) itemsRoot.querySelector('[data-matrix-quantity][data-variant="' + focusVariant + '"]')?.focus({ preventScroll: true });
        const restoredAnchor = anchorId && document.getElementById(anchorId);
        window.scrollTo({ top: restoredAnchor ? scrollTop + restoredAnchor.getBoundingClientRect().top - anchorTop : scrollTop, behavior: 'instant' });
      } catch (error) {
        if (!this.isConnected) return;
        this.status.textContent = error.message || 'Could not update cart.';
        if (productStatus) productStatus.textContent = error.message || 'Could not update quantity.';
        if (updated || uncertain) {
          const reload = this.element('button', 'link', 'Reload Cart'); reload.type = 'button'; reload.addEventListener('click', () => location.reload()); this.status.append(' ', reload);
        } else { this.querySelectorAll('[data-matrix-quantity]').forEach(control => { control.value = control.dataset.previous; control.classList.toggle('ld-empty-quantity', control.value === '0'); }); if (productStatus) productStatus.textContent = error.message || 'Could not update quantity.'; this.querySelector('[data-quantity-control][aria-busy]')?.removeAttribute('aria-busy'); controls.forEach((control, index) => { control.disabled = disabled[index]; }); checkout.forEach((button, index) => { button.disabled = checkoutDisabled[index]; }); this.querySelectorAll('[data-quantity-control] input').forEach(control => this.syncQuantityControl(control)); }
      } finally {
        if (loadingButton) { loadingButton.removeAttribute('aria-busy'); loadingButton.classList.remove('ld-cart-action-loading'); if (loadingLabel) loadingButton.setAttribute('aria-label', loadingLabel); }
        this.busy = false; this.removeAttribute('aria-busy');
      }
    }
  });
}



// Shared image feedback also covers quick-add and dynamically appended products.
(() => {
  const selector = '.card__media .media > img, .product__media img, .thumbnail img, .ld-variant-image img, .ld-bulk__image, .ld-bulk__option img';
  document.addEventListener('load', event => {
    const img = event.target;
    if (img.tagName !== 'IMG' || !img.matches(selector) || !img.naturalWidth) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    img.classList.add('ld-image-arrived');
  }, true);
  document.addEventListener('animationend', event => {
    if (event.animationName === 'ld-product-image-in') event.target.classList.remove('ld-image-arrived');
  }, true);
})();

// Retail cards use the same Shopify cart notification as the standard product form.
